/** @jsxImportSource @emotion/react */
import classNames from 'classnames';
import { Formik, FormikConfig, FormikProps } from 'formik';
import { modalButton, modalError, modalInputStyle } from 'modules/template-editor/ui/components/elements/modals/styles';
import React from 'react';
import UDFormInput from 'ud-form/components/input';
import { UDText } from 'ud-ui/components';
import { Button } from 'ud-ui/components/button/styles';
import Modal from 'ud-ui/components/modal';
import * as yup from 'yup';

export type DuplicateTemplateFormValues = {
  /** Empty means "keep the source name" — the placeholder shows what that is. */
  name: string;
};

type Props = FormikConfig<DuplicateTemplateFormValues> & {
  /** Name of the template being copied; shown as the input placeholder. */
  sourceName: string;
  isLoading: boolean;
  /** Error from the failed attempt (the name is taken, the request died, …). */
  errorMessage?: string | null;
};

export const DUPLICATE_TEMPLATE_INITIAL_VALUES: DuplicateTemplateFormValues = { name: '' };

// The name is optional here: leaving it blank copies under the source name. That
// only works when the copy lands in another scope (preset → tournament); inside
// one tournament the DB unique rule rejects it and the error comes back as
// `errorMessage` — there is no cheap way to know the taken names up front.
const validator = yup.object({
  name: yup.string().max(255, 'Слишком длинное название'),
});

function MediaManagerDuplicateTemplateForm(props: Props) {
  const { sourceName, isLoading, errorMessage, ...rest } = props;

  const getError = (formProps: FormikProps<DuplicateTemplateFormValues>): string | undefined =>
    (formProps.touched.name ? formProps.errors.name : undefined) ?? errorMessage ?? undefined;

  return (
    <Formik validateOnChange validationSchema={validator} {...rest}>
      {(formProps) => (
        <form onSubmit={formProps.handleSubmit} className="w-[450px]">
          <Modal.Body className="flex flex-col gap-3">
            <UDText isNew type="body-3" className="text-gray-400">
              Копия попадёт в шаблоны вашего турнира. Оставьте поле пустым, чтобы сохранить исходное название.
            </UDText>
            <UDFormInput readOnly={isLoading} name="name" autoFocus css={modalInputStyle} placeholder={sourceName} />
          </Modal.Body>
          <Modal.Footer justify="start">
            <Button
              type="submit"
              className={classNames('flex-center', isLoading && 'loading')}
              disabled={!formProps.isValid || isLoading}
              css={modalButton}
            >
              Дублировать
            </Button>
            <span css={modalError}>{getError(formProps)}</span>
          </Modal.Footer>
        </form>
      )}
    </Formik>
  );
}

export default MediaManagerDuplicateTemplateForm;
